/**
 * Courier Merchants — the partner restaurants we deliver for, and the email routing
 * that attributes their orders.
 *
 * This screen is what makes the system operable: email→merchant matching is explicit
 * config by design (never a fuzzy match on the restaurant name in the body, because a
 * wrong match dispatches a courier we pay for to the wrong city), so without a way to
 * edit these rows a new merchant needs raw SQL.
 *
 * `autoDispatch` is the kill switch and starts OFF. It is shown as a plain, obvious
 * toggle with the consequence written next to it, because turning it on is the moment
 * this system starts spending money without a human in the loop.
 */
import React, { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  Box, Container, Typography, Paper, Chip, Button, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Grid,
  FormControlLabel, Switch, MenuItem, IconButton, Divider, List, ListItemButton,
  Autocomplete,
} from '@mui/material'
import DataTable from 'react-data-table-component'
import RefreshIcon from '@mui/icons-material/Refresh'
import StorefrontIcon from '@mui/icons-material/Storefront'
import AddIcon from '@mui/icons-material/Add'
import ConfigurableValues from '../config/constants'
import { searchPlaces } from '../utils/mapbox'
import { restaurants as RESTAURANTS_QUERY } from '../apollo'

const MERCHANTS = gql`
  query CourierMerchants {
    courierMerchants {
      _id name phone email address latitude longitude timezone
      fdMerchantId provider matchFromAddress
      autoDispatch dispatchPolicy leadTimeMinutes maxAutoOrderTotal enabled notes
      rushmyfoodRestaurantId linkedRestaurantName
    }
  }
`
const RESTAURANTS = gql`${RESTAURANTS_QUERY}`
const SAVE = gql`
  mutation SaveMerchant($input: CourierMerchantInput!) {
    courierSaveMerchant(input: $input) { _id name autoDispatch enabled }
  }
`

const PROVIDER_LABELS = { EDINING: 'eDiningExpress', MENUSIFU: 'MenuSifu', OTHER: 'Other' }
// AT_IMPORT is a legacy value some existing rows still carry — it never
// actually dispatched any differently than LEAD_TIME (dispatchAt is always
// computed off the lead-time schedule either way), so it reads as the same
// label here rather than implying a distinction that was never real.
const POLICY_LABELS = { MANUAL: 'Manual send', LEAD_TIME: 'Auto — timed', AT_IMPORT: 'Auto — timed' }

const EMPTY = {
  name: '', phone: '', email: '', address: '', latitude: '', longitude: '',
  timezone: 'America/New_York', fdMerchantId: '', provider: 'EDINING',
  matchFromAddress: '',
  autoDispatch: false, dispatchPolicy: 'MANUAL', leadTimeMinutes: 12,
  maxAutoOrderTotal: '', enabled: true, notes: '',
  rushmyfoodRestaurantId: null, linkedRestaurantName: '',
}

export default function CourierMerchants() {
  const { MAPBOX_TOKEN } = ConfigurableValues()
  const { data, loading, refetch } = useQuery(MERCHANTS, { fetchPolicy: 'cache-and-network' })
  const [save, saveState] = useMutation(SAVE)
  const [form, setForm] = useState(null)
  // Only fetched when the edit dialog is actually open — this list can be large
  // and the picker is the one place on this whole screen that needs it.
  const restaurantsQ = useQuery(RESTAURANTS, { fetchPolicy: 'cache-first', skip: !form })
  const [msg, setMsg] = useState(null)
  const [predictions, setPredictions] = useState([])
  const [showPredictions, setShowPredictions] = useState(false)
  const searchTimer = useRef(null)

  const merchants = data?.courierMerchants ?? []
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }))

  // Same Mapbox autocomplete TruckStops.jsx uses — picking a suggestion is what
  // supplies coordinates. Typing a raw address still saves fine; it's just a
  // pickup point nobody has geocoded yet, so the wrong-town sanity check in
  // validate.js has nothing to compare a dropoff against until someone does.
  const onAddressChange = useCallback((value) => {
    setForm((f) => ({ ...f, address: value, latitude: '', longitude: '' }))
    if (searchTimer.current) clearTimeout(searchTimer.current)
    if (value.length > 2) {
      searchTimer.current = setTimeout(async () => {
        try {
          const results = await searchPlaces(value, MAPBOX_TOKEN, { limit: 5 })
          setPredictions(results)
          setShowPredictions(results.length > 0)
        } catch (_) { setShowPredictions(false) }
      }, 300)
    } else {
      setShowPredictions(false)
    }
  }, [MAPBOX_TOKEN])

  const openNew = () => { setForm({ ...EMPTY }); setMsg(null); setShowPredictions(false) }
  const openEdit = (m) => {
    setForm({
      ...EMPTY, ...m,
      latitude: m.latitude ?? '', longitude: m.longitude ?? '',
      maxAutoOrderTotal: m.maxAutoOrderTotal ?? '',
      phone: m.phone ?? '', email: m.email ?? '', address: m.address ?? '',
      fdMerchantId: m.fdMerchantId ?? '', matchFromAddress: m.matchFromAddress ?? '',
      notes: m.notes ?? '',
      // AT_IMPORT never actually dispatched any differently than LEAD_TIME —
      // dispatchAt is always computed off the lead-time schedule regardless of
      // which of the two a merchant was set to, so the "instant" option never
      // did what its own label claimed. Normalized here rather than kept as a
      // third dropdown choice with no real behavior behind it. A merchant
      // still on the old value (like WitchCity Hibachi) shows correctly as
      // "timed" instead of an empty field, and re-saving cleans it up.
      dispatchPolicy: m.dispatchPolicy === 'AT_IMPORT' ? 'LEAD_TIME' : (m.dispatchPolicy || 'MANUAL'),
    })
    setMsg(null)
    setShowPredictions(false)
  }

  const onSave = async () => {
    setMsg(null)
    try {
      const input = {
        ...(form._id ? { _id: form._id } : {}),
        name: form.name.trim(),
        phone: form.phone || null,
        email: form.email || null,
        address: form.address || null,
        latitude: form.latitude === '' ? null : Number(form.latitude),
        longitude: form.longitude === '' ? null : Number(form.longitude),
        timezone: form.timezone || 'America/New_York',
        fdMerchantId: form.fdMerchantId || null,
        provider: form.provider,
        // matchToAlias/matchSubjectRegex are always cleared: a "dedicated alias"
        // only means anything if it's checked against the actual recipient, and
        // every restaurant here forwards to the same shared orders@rushmyfood.com
        // — that field was matching on the ONE thing every merchant has in
        // common, not what tells them apart. One address, read from inside the
        // forwarded email itself, is the only signal that's ever actually unique
        // per restaurant.
        matchToAlias: null,
        matchFromAddress: form.matchFromAddress || null,
        matchSubjectRegex: null,
        autoDispatch: !!form.autoDispatch,
        dispatchPolicy: form.dispatchPolicy,
        leadTimeMinutes: Number(form.leadTimeMinutes) || 12,
        maxAutoOrderTotal: form.maxAutoOrderTotal === '' ? null : Number(form.maxAutoOrderTotal),
        enabled: !!form.enabled,
        notes: form.notes || null,
        rushmyfoodRestaurantId: form.rushmyfoodRestaurantId || null,
      }
      await save({ variables: { input } })
      await refetch()
      setForm(null)
      setMsg({ severity: 'success', text: `Saved ${input.name}` })
    } catch (e) {
      setMsg({ severity: 'error', text: `Save failed: ${e.message}` })
    }
  }

  // Without an email address on file, every order from this merchant lands
  // unmatched in the review queue.
  const unroutable = (m) => !m.matchFromAddress
  const undispatchable = (m) => !m.fdMerchantId

  const columns = [
    {
      name: 'Merchant', grow: 1,
      cell: (r) => (
        <Box sx={{ lineHeight: 1.3, py: 0.5 }}>
          <strong>{r.name}</strong>
          {r.linkedRestaurantName && (
            <div style={{ fontSize: 11, color: '#0369a1' }}>↔ {r.linkedRestaurantName}</div>
          )}
        </Box>
      ),
    },
    { name: 'Order system', width: '130px', selector: (r) => PROVIDER_LABELS[r.provider] || r.provider },
    {
      name: 'Email address', grow: 2,
      cell: (r) => (unroutable(r)
        ? <span style={{ color: '#b45309' }}>none set — orders will not match</span>
        : <span style={{ fontSize: 13 }}>{r.matchFromAddress}</span>),
    },
    {
      name: 'First Delivery', width: '150px',
      cell: (r) => (undispatchable(r)
        ? <span style={{ color: '#b45309' }}>no merchant id</span>
        : <span style={{ fontSize: 13 }}>{r.fdMerchantId}</span>),
    },
    {
      name: 'Sending', width: '150px',
      cell: (r) => (
        <Chip
          size="small"
          label={r.autoDispatch ? POLICY_LABELS[r.dispatchPolicy] || 'Auto' : 'Manual send'}
          sx={{ bgcolor: r.autoDispatch ? '#dcfce7' : '#f1f5f9', color: r.autoDispatch ? '#14532d' : '#475569', fontWeight: 600 }}
        />
      ),
    },
    {
      name: 'Watching emails', width: '130px',
      cell: (r) => <Chip size="small" label={r.enabled ? 'yes' : 'no'} sx={{ bgcolor: r.enabled ? '#e0f2fe' : '#fee2e2', fontWeight: 600 }} />,
    },
    { name: '', width: '80px', cell: (r) => <Button size="small" onClick={() => openEdit(r)} sx={{ textTransform: 'none' }}>Edit</Button> },
  ]

  return (
    <Container maxWidth={false} sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <StorefrontIcon sx={{ color: '#FF3008' }} />
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Courier Merchants</Typography>
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<AddIcon />} variant="contained" onClick={openNew} sx={{ textTransform: 'none' }}>
          Add merchant
        </Button>
        <IconButton onClick={() => refetch()}><RefreshIcon /></IconButton>
      </Box>

      {msg && <Alert severity={msg.severity} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}

      <Paper>
        <DataTable
          columns={columns}
          data={merchants}
          progressPending={loading}
          progressComponent={<Box sx={{ py: 4 }}><CircularProgress /></Box>}
          noDataComponent={
            <Box sx={{ py: 5, color: '#64748b', textAlign: 'center' }}>
              No merchants yet. Until one exists, every incoming order email lands in
              the inbox as &quot;no merchant matched&quot;.
            </Box>
          }
          highlightOnHover
          dense
        />
      </Paper>

      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="md" fullWidth>
        <DialogTitle>{form?._id ? `Edit ${form.name}` : 'Add merchant'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Name" fullWidth size="small" value={form?.name || ''} onChange={set('name')} required />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField label="Phone" fullWidth size="small" value={form?.phone || ''} onChange={set('phone')} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select label="Order system they use" fullWidth size="small"
                value={form?.provider || 'EDINING'} onChange={set('provider')}
                helperText="Controls how we read their order emails."
              >
                <MenuItem value="EDINING">{PROVIDER_LABELS.EDINING}</MenuItem>
                <MenuItem value="MENUSIFU">{PROVIDER_LABELS.MENUSIFU}</MenuItem>
                <MenuItem value="OTHER">Other / not supported yet</MenuItem>
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left" sx={{ fontSize: 12, color: '#64748b' }}>PICKUP LOCATION</Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                <TextField
                  label="Address" fullWidth size="small"
                  value={form?.address || ''} onChange={(e) => onAddressChange(e.target.value)}
                  helperText={form?.latitude !== '' && form?.latitude != null
                    ? 'Position set from the suggestion you picked.'
                    : 'Pick a suggestion to set exact coordinates, or type one and fill lat/lng by hand.'}
                />
                {showPredictions && (
                  <Paper sx={{ position: 'absolute', zIndex: 10, width: '100%', mt: 0.5, maxHeight: 200, overflow: 'auto' }}>
                    <List dense>
                      {predictions.map((p, i) => (
                        <ListItemButton key={i} onClick={() => {
                          setForm((f) => ({ ...f, address: p.placeName, latitude: p.lat, longitude: p.lng }))
                          setShowPredictions(false)
                        }}>
                          <Typography sx={{ fontSize: '0.85rem' }}>{p.placeName}</Typography>
                        </ListItemButton>
                      ))}
                    </List>
                  </Paper>
                )}
              </Box>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Latitude" fullWidth size="small" value={form?.latitude ?? ''} onChange={set('latitude')} />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField label="Longitude" fullWidth size="small" value={form?.longitude ?? ''} onChange={set('longitude')} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField label="Timezone" fullWidth size="small" value={form?.timezone || ''} onChange={set('timezone')} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                We use this to double-check delivery addresses aren&apos;t way off —
                for example if the map service gets confused and points to the wrong city.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left" sx={{ fontSize: 12, color: '#64748b' }}>HOW WE RECOGNIZE THEIR EMAILS</Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Their email address" fullWidth size="small"
                value={form?.matchFromAddress || ''} onChange={set('matchFromAddress')}
                placeholder="orders@theirsystem.com"
                helperText="The address their order system actually sends from — we read this straight out of the forwarded email, even when it arrives relayed through another address first."
              />
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left" sx={{ fontSize: 12, color: '#64748b' }}>LINK TO A RUSHMYFOOD RESTAURANT</Divider>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                size="small"
                loading={restaurantsQ.loading}
                options={restaurantsQ.data?.restaurants ?? []}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o._id === v._id}
                value={(restaurantsQ.data?.restaurants ?? []).find((r) => r._id === form?.rushmyfoodRestaurantId) || null}
                onChange={(_, selected) => setForm((f) => ({
                  ...f,
                  rushmyfoodRestaurantId: selected ? selected._id : null,
                  linkedRestaurantName: selected ? selected.name : '',
                }))}
                renderInput={(params) => <TextField {...params} label="This partner's own restaurant on rushmyfood (optional)" />}
              />
              <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
                Only if this restaurant ALSO sells on rushmyfood directly. Linking it lets them see
                these partner-order emails and their status in their own restaurant admin panel —
                it changes nothing about how orders are matched, parsed, or dispatched.
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <Divider textAlign="left" sx={{ fontSize: 12, color: '#64748b' }}>SENDING ORDERS TO THE COURIER</Divider>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="First Delivery's ID for this restaurant" fullWidth size="small"
                value={form?.fdMerchantId || ''} onChange={set('fdMerchantId')}
                helperText="Comes FROM First Delivery when they set this restaurant up as a pickup point — not something to make up. Nothing can be sent to a courier without it. Sandbox and production merchant ids are different — check with First Delivery you have the right one for whichever environment we're currently sending to."
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select label="When should we send it to the courier?" fullWidth size="small"
                value={form?.dispatchPolicy || 'MANUAL'} onChange={set('dispatchPolicy')}
              >
                <MenuItem value="MANUAL">Wait — I&apos;ll click Send myself</MenuItem>
                <MenuItem value="LEAD_TIME">Send automatically, {form?.leadTimeMinutes || 12} min before it&apos;s due</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="How many minutes early?" fullWidth size="small"
                value={form?.leadTimeMinutes ?? 12} onChange={set('leadTimeMinutes')}
                helperText="How many minutes before the promised time we send it to First Delivery."
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <TextField
                label="Never auto-send above $" fullWidth size="small"
                value={form?.maxAutoOrderTotal ?? ''} onChange={set('maxAutoOrderTotal')}
                helperText="Blank = no limit"
              />
            </Grid>

            <Grid item xs={12}>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: form?.autoDispatch ? '#f0fdf4' : '#f8fafc' }}>
                <FormControlLabel
                  control={<Switch checked={!!form?.autoDispatch} onChange={set('autoDispatch')} />}
                  label={<strong>Send orders automatically — no one has to click Send</strong>}
                />
                <Typography sx={{ fontSize: 12, color: '#475569' }}>
                  With this on, orders that pass every check go straight to the courier
                  with no human involved. Leave it off until you&apos;ve watched a few of
                  this restaurant&apos;s real orders come through correctly. (There&apos;s also a
                  master switch our engineers control — both have to be on for this to
                  actually send anything.)
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={!!form?.enabled} onChange={set('enabled')} />}
                label="Watch for this restaurant's emails"
              />
              <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                Different from the sending switch above. Off means we stop recognizing
                their emails entirely — orders won&apos;t even show up to review.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" fullWidth size="small" multiline minRows={2} value={form?.notes || ''} onChange={set('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForm(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!form?.name?.trim() || saveState.loading}
            onClick={onSave}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}
